import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { supabase } from './supabaseClient';
import Preloader from './Preloader';
import ReactDOMServer from 'react-dom/server';
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api';
import { FaUsers, FaUserCheck, FaFire, FaHouseFloodWater, FaHeart, FaTruck, FaTriangleExclamation, FaLocationArrow } from 'react-icons/fa6';

const libraries = ['places', 'marker'];

function ResponderMap() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [responders, setResponders] = useState([]);
  const [availabilityByResponderId, setAvailabilityByResponderId] = useState({});
  const [approvedAvailableByResponderId, setApprovedAvailableByResponderId] = useState({});

  const [selectedIncident, setSelectedIncident] = useState(null);
  const [availableForAssignment, setAvailableForAssignment] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const [stats, setStats] = useState({ available: 0, responding: 0 });
  const [busyResponderIds, setBusyResponderIds] = useState([]);

  const [mapCenter, setMapCenter] = useState({ lat: 14.5995, lng: 120.9842 }); // Metro Manila default

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA',
    libraries,
  });

  const mapOptions = useMemo(
    () => ({
      disableDefaultUI: true,
      zoomControl: true,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ],
    }),
    []
  );

  useEffect(() => {
    document.title = 'Responder Map';
  }, []);

  useEffect(() => {
    fetchData();

    // realtime: incidents, responder locations, availability
    const incidentsChannel = supabase
      .channel('public:responder_map_incidents')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'incident_reports' }, async () => {
        await fetchIncidents();
        await fetchRespondersAndAvailability();
      })
      .subscribe();

    const respondersChannel = supabase
      .channel('public:responder_map_responders')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'approved_responders' }, () => fetchRespondersAndAvailability())
      .subscribe();

    const availabilityChannel = supabase
      .channel('public:responder_map_availability')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'admin_responder_availability' }, () => fetchRespondersAndAvailability())
      .subscribe();

    const responsesChannel = supabase
      .channel('public:responder_map_responses')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'responder_incident_responses' }, () => fetchRespondersAndAvailability())
      .subscribe();

    return () => {
      supabase.removeChannel(incidentsChannel);
      supabase.removeChannel(respondersChannel);
      supabase.removeChannel(availabilityChannel);
      supabase.removeChannel(responsesChannel);
    };
  }, []);

  async function fetchData() {
    setLoading(true);
    setError('');
    await fetchIncidents();
    await fetchRespondersAndAvailability();
    setLoading(false);
  }

  async function fetchIncidents() {
    try {
      const { data, error } = await supabase
        .from('incident_reports')
        .select('id, type, location, status, created_at, location_coords')
        .in('status', ['active', 'responding']);
      if (error) throw error;

      const withCoords = (data || []).filter((i) => i.location_coords);
      setIncidents(withCoords);
      if (withCoords.length > 0) {
        setMapCenter({ lat: withCoords[0].location_coords.latitude, lng: withCoords[0].location_coords.longitude });
      }
    } catch (err) {
      console.error('Error fetching incidents', err);
      setError('Failed to load incidents');
      setIncidents([]);
    }
  }

  async function fetchRespondersAndAvailability() {
    try {
      const [
        { data: respondersData, error: respErr },
        { data: availabilityData, error: availErr },
        { data: responsesData, error: responsesErr },
      ] = await Promise.all([
        supabase
          .from('approved_responders')
          .select(
            '*'
          )
          .eq('is_active', true),
        supabase.from('admin_responder_availability').select('*'),
        supabase
          .from('responder_incident_responses')
          .select('responder_id,status,completed_at,incident_id')
          .in('status', ['accepted', 'responding', 'on_scene'])
      ]);
      if (respErr) throw respErr;
      if (availErr) throw availErr;
      if (responsesErr) throw responsesErr;

      const availById = {};
      (availabilityData || []).forEach((a) => {
        availById[a.responder_id] = a;
      });
      setAvailabilityByResponderId(availById);

      // Map of approved availability flag (from approved_responders.is_available)
      const approvedAvailById = {};
      (respondersData || []).forEach((r) => {
        approvedAvailById[r.id] = !!r.is_available;
      });
      setApprovedAvailableByResponderId(approvedAvailById);

      const respondersWithCoords = (respondersData || []).filter(
        (r) => r.current_location_lat && r.current_location_lng
      );
      setResponders(respondersWithCoords);

      // Stats
      // Count only responders who are marked available in both sources
      const availableCount = (availabilityData || []).filter((a) => a.availability_status === 'available' && approvedAvailById[a.responder_id]).length;
      const busyIds = new Set();
      // From availability table
      (availabilityData || []).forEach((a) => {
        if (a && ['responding', 'on_scene'].includes(a.availability_status)) {
          busyIds.add(a.responder_id);
        }
      });
      // From responses table (active if not completed)
      const activeIncidentIds = new Set((incidents || []).map(i => i.id));
      (responsesData || []).forEach((r) => {
        if (
          r && r.responder_id != null && !r.completed_at &&
          ['accepted', 'responding', 'on_scene'].includes(r.status) &&
          (r.incident_id ? activeIncidentIds.has(r.incident_id) : true)
        ) {
          busyIds.add(r.responder_id);
        }
      });
      setStats({ available: availableCount, responding: busyIds.size });
      setBusyResponderIds(Array.from(busyIds));
    } catch (err) {
      console.error('Error fetching responders/availability', err);
      setError('Failed to load responders');
      setResponders([]);
      setStats({ available: 0, responding: 0 });
    }
  }

  function getIncidentMarkerIcon(type) {
    let iconComponent;
    const size = 28;
    switch ((type || '').toLowerCase()) {
      case 'fire':
        iconComponent = <FaFire color="#e65100" size={size} />;
        break;
      case 'flood':
        iconComponent = <FaHouseFloodWater color="#1976d2" size={size} />;
        break;
      case 'medical':
        iconComponent = <FaHeart color="#d32f2f" size={size} />;
        break;
      case 'rescue':
        iconComponent = <FaTruck color="#388e3c" size={size} />;
        break;
      default:
        iconComponent = <FaTriangleExclamation color="#f59e0b" size={size} />;
    }
    const svgString = ReactDOMServer.renderToString(iconComponent);
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgString)}`;
  }

  function getResponderMarkerIcon(responderId) {
    const availability = availabilityByResponderId[responderId]?.availability_status;
    const approvedAvailable = approvedAvailableByResponderId[responderId] !== false; // default to true if unknown
    let color = '#16a34a'; // default available

    if (availability === 'responding') {
      color = '#f59e0b';
    } else if (availability === 'on_scene') {
      color = '#ef4444';
    } else if (!approvedAvailable) {
      // Show unavailable (from approved_responders) as gray if not actively responding/on_scene
      color = '#9ca3af';
    }

    const size = 48; // Bigger for visibility
    const center = size / 2;
    const mainRadius = center - 6;
    const glowRadius = center - 2;

    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <defs>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.25"/>
          </filter>
        </defs>
        <g filter="url(#shadow)">
          <circle cx="${center}" cy="${center}" r="${glowRadius}" fill="none" stroke="${color}" stroke-width="6" opacity="0.25" />
          <circle cx="${center}" cy="${center}" r="${mainRadius}" fill="${color}" stroke="#ffffff" stroke-width="4" />
          <text x="50%" y="50%" text-anchor="middle" dominant-baseline="central" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="20" fill="#ffffff">R</text>
        </g>
      </svg>`;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgString)}`;
  }

  async function openAssignment(incident) {
    setSelectedIncident(incident);
    // Fetch responders and filter with availability state for true availability
    const { data, error } = await supabase
      .from('approved_responders')
      .select('*')
      .eq('is_active', true)
      .order('first_name');
    if (!error) {
      const filtered = (data || []).filter((responder) => {
        const a = availabilityByResponderId[responder.id];
        // Must be available in live availability AND allowed by approved_responders flag
        const approvedAvail = approvedAvailableByResponderId[responder.id] !== false;
        return approvedAvail && a && a.availability_status === 'available';
      });
      setAvailableForAssignment(filtered);
    }
  }

  async function assignResponder(responder) {
    if (!selectedIncident) return;
    setAssignLoading(true);
    try {
      // Safety check: ensure responder is still allowed by approved_responders flag
      if (approvedAvailableByResponderId[responder.id] === false) {
        alert('This responder is currently set as unavailable by admins and cannot be assigned.');
        return;
      }

      // Block if responder is busy on ANY active response
      const [{ data: activeAvail, error: availCheckErr }, { data: activeResp, error: respCheckErr }] = await Promise.all([
        supabase
          .from('admin_responder_availability')
          .select('availability_status,current_incident_id')
          .eq('responder_id', responder.id)
          .maybeSingle(),
        supabase
          .from('responder_incident_responses')
          .select('id,status,completed_at')
          .eq('responder_id', responder.id)
          .in('status', ['accepted', 'responding', 'on_scene'])
      ]);
      if (availCheckErr) throw availCheckErr;
      if (respCheckErr) throw respCheckErr;

      const isBusyByAvailability = activeAvail && ['responding', 'on_scene'].includes(activeAvail.availability_status);
      const isBusyByResponses = Array.isArray(activeResp) && activeResp.some(r => !r.completed_at && ['accepted', 'responding', 'on_scene'].includes(r.status));
      if (isBusyByAvailability || isBusyByResponses) {
        alert('Responder is currently assigned to another incident and cannot be reassigned until they are available.');
        return;
      }

      // Already assigned to this incident?
      const { data: existing, error: checkErr } = await supabase
        .from('responder_incident_responses')
        .select('id')
        .eq('responder_id', responder.id)
        .eq('incident_id', selectedIncident.id)
        .in('status', ['accepted', 'responding', 'on_scene'])
        .maybeSingle();
      if (checkErr && checkErr.code !== 'PGRST116') throw checkErr;
      if (existing) {
        alert('Responder is already assigned to this incident.');
        return;
      }

      const { error: insertErr } = await supabase.from('responder_incident_responses').insert([
        {
          responder_id: responder.id,
          incident_id: selectedIncident.id,
          accepted_at: new Date().toISOString(),
          status: 'accepted',
          assignment_type: 'admin_assigned',
        },
      ]);
      if (insertErr) throw insertErr;

      // Best-effort availability rpc
      try {
        await supabase.rpc('update_responder_availability', {
          responder_bigint: responder.id,
          new_status: 'responding',
          incident_id: selectedIncident.id,
        });
      } catch (e) {
        // ignore
      }

      await fetchRespondersAndAvailability();
      alert(`Assigned ${responder.first_name} ${responder.last_name}`);
    } catch (e) {
      console.error('Assignment failed', e);
      alert('Failed to assign responder');
    } finally {
      setAssignLoading(false);
    }
  }

  if (loading) return <Preloader text="Loading Responder Map..." />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div className="header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {/* Mobile Hamburger Menu */}
                <MobileHamburger
                  isOpen={showMobileMenu}
                  onToggle={() => setShowMobileMenu(prev => !prev)}
                />
                <div className="title-text">Responder Map</div>
              </div>
            </div>
          </div>
          <ProfileMenu />
        </div>

          <p style={{ color: '#7f8c8d', marginBottom: '24px' }}>
            Monitor incidents and responder locations, and assign responders.
          </p>

          {/* Mobile Dropdown Menu */}
          <MobileMenu 
            isOpen={showMobileMenu} 
            onClose={() => setShowMobileMenu(false)} 
          />

        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', minWidth: 220, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <div style={{ color: '#6b7280', marginBottom: 4 }}>Responders Available</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 34, fontWeight: 'bold', color: '#16a34a' }}>{stats.available}</span>
              <FaUserCheck style={{ color: '#16a34a', fontSize: 26 }} />
            </div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', minWidth: 220, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <div style={{ color: '#6b7280', marginBottom: 4 }}>Currently Responding</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 34, fontWeight: 'bold', color: '#f59e0b' }}>{stats.responding}</span>
              <FaUsers style={{ color: '#f59e0b', fontSize: 26 }} />
            </div>
          </div>
        </div>

        {/* Map */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: 600, borderRadius: 12 }}
              center={mapCenter}
              zoom={12}
              options={mapOptions}
            >
              {/* Incident markers */}
              {incidents.map((incident) => (
                <Marker
                  key={incident.id}
                  position={{
                    lat: incident.location_coords.latitude,
                    lng: incident.location_coords.longitude,
                  }}
                  icon={{ url: getIncidentMarkerIcon(incident.type), scaledSize: new window.google.maps.Size(28, 28) }}
                  onClick={() => openAssignment(incident)}
                />
              ))}

              {/* Responder markers */}
              {responders.map((r) => (
                <Marker
                  key={r.id}
                  position={{ lat: Number(r.current_location_lat), lng: Number(r.current_location_lng) }}
                  zIndex={999}
                  icon={{
                    url: getResponderMarkerIcon(r.id),
                    scaledSize: new window.google.maps.Size(48, 48),
                    anchor: new window.google.maps.Point(24, 24),
                  }}
                />
              ))}
            </GoogleMap>
          ) : (
            <div style={{ height: 600, borderRadius: 12, background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading map...</div>
          )}
        </div>

        {/* Assignment Panel */}
        {selectedIncident && (
          <div style={{ marginTop: 20, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700 }}>Assign Responders — {selectedIncident.type} @ {selectedIncident.location}</div>
              <button onClick={() => setSelectedIncident(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>Close</button>
            </div>
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {availableForAssignment.length === 0 && (
                <div style={{ color: '#6b7280' }}>No available responders at the moment.</div>
              )}
              {availableForAssignment.map((responder) => (
                <div key={responder.id} style={{ border: '1px solid #eef2f7', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontWeight: 600 }}>{responder.first_name} {responder.last_name}</div>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>{responder.profession || 'Responder'}</div>
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ color: '#6b7280', fontSize: 12 }}>ID: {responder.responder_id}</div>
                    <button
                      onClick={() => assignResponder(responder)}
                      disabled={assignLoading}
                      style={{
                        background: '#08b1c7',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      {assignLoading ? 'Assigning...' : 'Assign'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResponderMap;


